import React, { useCallback, useEffect, useRef, useState } from 'react';
import { carteraApi } from './api';
import { ModalPortal } from './ModalPortal';

/*
 * Mensaje rápido: el modal del botón "Enviar mensaje" de la matriz.
 *
 * Manda DIRECTO (no encola), igual que el chip de plantillas de
 * Conversaciones, que es el camino que el jefe ya usa hoy. La diferencia está
 * en lo que hay ANTES del envío: el backend decide qué plantilla toca, arma la
 * vista previa con los datos reales del cliente y aplica los cortes que al
 * chip del chat le faltan (horario legal, número interno de avisos, etapa
 * extrajudicial).
 *
 * ── Reparto de responsabilidades ─────────────────────────────────────────
 * Este componente NO decide nada: pinta lo que el backend respondió y manda
 * la clave elegida. Toda la lógica —qué plantilla aplica, cuál se
 * preselecciona, qué bloquea, qué advierte— vive en MensajeRapidoService, y
 * el envío la revalida completa. Que el front no recalcule es deliberado: dos
 * verdades sobre lo mismo es exactamente cómo se llega a un panel que promete
 * un texto y manda otro.
 *
 * 🔴 La vista previa viene del backend y es APROXIMADA: el cuerpo aprobado en
 * Meta no vive en el repositorio, solo una transcripción. Se etiqueta en
 * pantalla para que nadie la lea como el texto literal.
 */

const uuid = () => (
  // crypto.randomUUID no existe en todos los navegadores que usa el equipo
  // (ni en jsdom viejo), así que hay respaldo. El valor solo necesita ser
  // único por intento, no criptográfico.
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    })
);

const errorApi = (error, fallback) => error?.response?.data?.message
  || Object.values(error?.response?.data?.errors || {})?.flat()?.[0]
  || fallback;

const telefonoBonito = (e164) => {
  if (!e164) return null;
  const sinLada = e164.startsWith('52') ? e164.slice(2) : e164;
  return sinLada.length === 10
    ? `+52 ${sinLada.slice(0, 2)} ${sinLada.slice(2, 6)} ${sinLada.slice(6)}`
    : `+${e164}`;
};

export const MensajeRapidoModal = ({ creditoId, onClose, onEnviado }) => {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [elegida, setElegida] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(null);

  // Un request_id por apertura del modal: hace idempotente el intento, así que
  // un doble clic o un reintento de axios devuelve lo ya enviado en vez de
  // mandar un segundo WhatsApp. Si el cobrador cambia de plantilla se renueva
  // (es otro mensaje, no un reintento del mismo).
  const requestId = useRef(uuid());

  const cargar = useCallback(async (signal) => {
    setCargando(true);
    setError('');
    try {
      const res = await carteraApi.getMensajeRapido(creditoId, signal);
      const payload = res?.data?.data || null;
      setData(payload);
      setElegida(payload?.sugerida || null);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED') return;
      setError(errorApi(e, 'No se pudieron cargar las plantillas de este contrato.'));
    } finally {
      if (!signal?.aborted) setCargando(false);
    }
  }, [creditoId]);

  useEffect(() => {
    const controller = new AbortController();
    cargar(controller.signal);
    return () => controller.abort();
  }, [cargar]);

  // Cierre con ESC. Ningún modal de Cartera lo tenía; se copia el patrón de
  // DrillPopover. No cierra mientras hay un envío en vuelo: perder de vista
  // el resultado de un mensaje que ya salió es peor que un modal pegado.
  useEffect(() => {
    const alTeclear = (evento) => {
      if (evento.key === 'Escape' && !enviando) onClose();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onClose, enviando]);

  const bloqueo = data?.bloqueo || null;
  const plantillas = data?.plantillas || [];
  const seleccionada = plantillas.find((p) => p.key === elegida) || null;
  // `permitida` (¿se puede mandar?), no `aplica` (¿es la que corresponde?):
  // el cobrador puede elegir otra a propósito, con la advertencia enfrente.
  const puedeEnviar = !bloqueo && !!seleccionada?.permitida && !enviando && !enviado;

  const enviar = async () => {
    // Guarda de reentrada de verdad, no solo `disabled`: el atributo es estado
    // de React y no es un candado.
    if (enviando || !puedeEnviar) return;
    setEnviando(true);
    setError('');
    try {
      const res = await carteraApi.enviarMensajeRapido(creditoId, {
        plantilla: seleccionada.key,
        request_id: requestId.current,
      });
      setEnviado(res?.data?.data || { plantilla_label: seleccionada.label });
      onEnviado?.();
    } catch (e) {
      setError(errorApi(e, 'No se pudo enviar el mensaje.'));
    } finally {
      setEnviando(false);
    }
  };

  const elegir = (key) => {
    if (enviando || enviado) return;
    setElegida(key);
    requestId.current = uuid();
  };

  const contrato = data?.credito;

  return (
    <ModalPortal>
      <div
        className="av-modal-backdrop"
        role="presentation"
        onMouseDown={(evento) => {
          if (evento.target === evento.currentTarget && !enviando) onClose();
        }}
      >
        <div
          className="dc-template-modal mtz-msg-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Enviar mensaje de cobranza"
          onMouseDown={(evento) => evento.stopPropagation()}
        >
          <div className="av-modal-head">
            <div>
              <h3>Enviar mensaje</h3>
              <p>
                {cargando
                  ? 'Cargando…'
                  : `${contrato?.clave_contrato || ''} · ${contrato?.cliente || ''}`}
              </p>
            </div>
            <button type="button" onClick={onClose} disabled={enviando} aria-label="Cerrar">
              <i className="pi pi-times" />
            </button>
          </div>

          {cargando && (
            <div className="av-state">
              <i className="pi pi-spin pi-spinner" /> Preparando con los datos de ahora…
            </div>
          )}

          {!cargando && error && !data && (
            <div className="av-inline-alert error">
              <i className="pi pi-times-circle" />{error}
            </div>
          )}

          {!cargando && data && (
            <>
              {/* Destino real y normalizado, no "el teléfono del cliente": la
                  cascada que resuelve el número puede devolver uno distinto al
                  que un humano ve en el contrato. */}
              <div className="mtz-msg-destino">
                <span><i className="pi pi-whatsapp" /> Se enviará a</span>
                <b className="tnum">
                  {telefonoBonito(data.destinatario?.telefono_e164) || 'sin teléfono'}
                </b>
                {contrato?.dias_atraso > 0 && (
                  <em>{contrato.dias_atraso} días de atraso · {contrato.cuotas_vencidas} vencidas</em>
                )}
              </div>

              {bloqueo && (
                <div className="av-inline-alert error">
                  <i className="pi pi-ban" />{bloqueo.texto}
                </div>
              )}

              {(data.advertencias || []).map((aviso) => (
                <div className="av-inline-alert warn" key={aviso.clave}>
                  <i className="pi pi-exclamation-triangle" />{aviso.texto}
                </div>
              ))}

              {enviado && (
                <div className="av-inline-alert ok">
                  <i className="pi pi-check-circle" />
                  Se envió «{enviado.plantilla_label}».
                </div>
              )}

              {!bloqueo && (
                <>
                  <div className="dc-template-options">
                    {plantillas.map((plantilla) => (
                      <button
                        type="button"
                        key={plantilla.key}
                        className={[
                          elegida === plantilla.key ? 'active' : '',
                          // Se puede mandar, pero no es la que corresponde.
                          plantilla.permitida && !plantilla.aplica ? 'mtz-msg-fuera' : '',
                        ].filter(Boolean).join(' ')}
                        disabled={!plantilla.permitida || enviando || Boolean(enviado)}
                        title={plantilla.motivo || plantilla.motivo_no_aplica || plantilla.label}
                        onClick={() => elegir(plantilla.key)}
                      >
                        {plantilla.label}
                        {plantilla.key === data.sugerida && (
                          <span className="mtz-msg-sugerida">sugerida</span>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="dc-template-preview">
                    {seleccionada?.vista_previa
                      ? <p>{seleccionada.vista_previa}</p>
                      : (
                        <span className="error">
                          <i className="pi pi-exclamation-circle" />
                          {seleccionada?.motivo
                            || 'Elige una plantilla para ver el mensaje.'}
                        </span>
                      )}
                  </div>

                  {/* Eligió una que se puede mandar pero no es la que toca. Se
                      avisa sin estorbar: la decisión es suya, y el texto de
                      arriba ya muestra exactamente lo que va a recibir. */}
                  {seleccionada?.permitida && !seleccionada?.aplica && seleccionada?.motivo_no_aplica && (
                    <div className="av-inline-alert warn">
                      <i className="pi pi-exclamation-triangle" />
                      No es la plantilla que corresponde: {seleccionada.motivo_no_aplica} Revisa el
                      texto antes de enviarlo.
                    </div>
                  )}

                  {data.preview_aproximado && seleccionada?.vista_previa && (
                    <p className="mtz-msg-nota">
                      Texto aproximado: el cuerpo final lo pone WhatsApp, y el mensaje lleva
                      además los botones de respuesta.
                    </p>
                  )}
                </>
              )}

              {error && data && (
                <div className="av-inline-alert error">
                  <i className="pi pi-times-circle" />{error}
                </div>
              )}
            </>
          )}

          <div className="av-confirm-actions">
            <button type="button" onClick={onClose} disabled={enviando}>
              {enviado ? 'Cerrar' : 'Cancelar'}
            </button>
            {!enviado && (
              <button type="button" className="primary" disabled={!puedeEnviar} onClick={enviar}>
                <i className={`pi ${enviando ? 'pi-spin pi-spinner' : 'pi-send'}`} />
                {enviando ? ' Enviando…' : ' Enviar ahora'}
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default MensajeRapidoModal;
